import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp } from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";

const CHAPTERS = [
  { n: "01", glow: "#00529B", t_nl: "Aseptische productie", t_en: "Aseptic production", b_nl: "Elke batch wordt verpakt onder ISO Class 5 laminar-flow condities. Geen uitzonderingen, geen shortcuts.", b_en: "Every batch is packaged under ISO Class 5 laminar flow conditions. No exceptions, no shortcuts." },
  { n: "02", glow: "#D12A2F", t_nl: "Third-party validatie", t_en: "Third-party validation", b_nl: "HPLC en massaspectrometrie door onafhankelijke Europese laboratoria — rapport bij iedere batch beschikbaar.", b_en: "HPLC and mass spectrometry by independent European laboratories — report available for every batch." },
  { n: "03", glow: "#5A2A82", t_nl: "Transparante herkomst", t_en: "Transparent origin", b_nl: "Grondstoffen uit gecertificeerde GMP-faciliteiten. Volledige chain of custody, gedocumenteerd per lot.", b_en: "Raw materials from certified GMP facilities. Full chain of custody, documented per lot." },
  { n: "04", glow: "#007A78", t_nl: "Snelle EU-verzending", t_en: "Fast EU shipping", b_nl: "Verzonden vanuit Nederland binnen 24 uur. Getrackt, temperatuur-gecontroleerd, discreet verpakt.", b_en: "Dispatched from the Netherlands within 24 hours. Tracked, temperature-controlled, discreetly packaged." },
];

function Chapter({ c, i, lang }) {
  const rowRef = useRef();
  const { scrollYProgress } = useScroll({ target: rowRef, offset: ["start end", "end start"] });
  const nY = useTransform(scrollYProgress, [0, 1], ["40%", "-40%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0.6]);

  return (
    <div ref={rowRef} className="relative grid grid-cols-1 md:grid-cols-12 gap-8 py-14 md:py-20 border-t border-neutral-800">
      {/* Colored glow */}
      <motion.div
        className="absolute -left-32 top-1/2 -translate-y-1/2 w-[480px] h-[480px] pointer-events-none"
        style={{
          background: `radial-gradient(circle at center, ${c.glow}55, transparent 65%)`,
          filter: "blur(60px)",
          opacity,
        }}
      />

      {/* Chapter huge number */}
      <div className="md:col-span-3 relative">
        <motion.div
          style={{ y: nY }}
          className="font-serif-display italic text-[140px] md:text-[220px] leading-none text-white/10"
        >
          {c.n}
        </motion.div>
        <div className="absolute top-0 left-0 font-mono-label text-[10px]" style={{ color: c.glow }}>
          [ CHAPTER · {c.n} ]
        </div>
      </div>

      <div className="md:col-span-9 md:pl-6 relative z-10">
        <FadeUp>
          {/* Title with per-char stagger */}
          <motion.h3
            className="font-serif-display font-light text-4xl md:text-6xl leading-[1.05] tracking-tight text-[#F7F7F6]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {(lang === "nl" ? c.t_nl : c.t_en).split(" ").map((w, wi) => (
              <motion.span
                key={wi}
                className="inline-block mr-3"
                initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: 0.1 + wi * 0.06, ease: [0.16, 1, 0.3, 1] }}
              >
                {w}
              </motion.span>
            ))}
          </motion.h3>
        </FadeUp>

        <FadeUp delay={0.2}>
          <p className="mt-8 text-neutral-400 max-w-2xl leading-relaxed text-lg">
            {lang === "nl" ? c.b_nl : c.b_en}
          </p>
        </FadeUp>

        {/* Animated accent underline */}
        <motion.div
          className="mt-8 h-px origin-left"
          style={{ background: `linear-gradient(90deg, ${c.glow}, transparent)` }}
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.4, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </div>
  );
}

export default function ManifestoWow() {
  const { t, lang } = useI18n();
  return (
    <section
      data-testid="home-manifesto"
      className="relative py-24 md:py-32 overflow-hidden bg-[#0A0A0B] text-[#F7F7F6]"
    >
      {/* Ambient aurora backdrop */}
      <motion.div
        className="absolute -top-40 -left-40 w-[720px] h-[720px] pointer-events-none"
        style={{
          background: "radial-gradient(circle at center, rgba(0,82,155,0.22), transparent 65%)",
          filter: "blur(80px)",
        }}
        animate={{ scale: [1, 1.2, 1], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-0 -right-40 w-[720px] h-[720px] pointer-events-none"
        style={{
          background: "radial-gradient(circle at center, rgba(90,42,130,0.2), transparent 65%)",
          filter: "blur(90px)",
        }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      {/* Dotted grid backdrop with fade */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.09]"
        style={{
          backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 78%)",
        }}
      />

      {/* Slow shimmer sweep */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <motion.div
          className="absolute left-0 right-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)" }}
          animate={{ y: [-200, 900, -200] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-3">
            <p className="font-mono-label text-[11px] text-neutral-500">[ 04 · {t("manifesto_over").toUpperCase()} ]</p>
          </div>
          <div className="md:col-span-9">
            <FadeUp>
              <h2 className="font-serif-display font-light text-4xl md:text-7xl leading-[1.02] tracking-tight">
                {(lang === "nl"
                  ? ["Wat wij onder", "onderzoekskwaliteit", "verstaan."]
                  : ["What research grade", "means", "to us."]
                ).map((line, i) => (
                  <span key={i} className="block overflow-hidden">
                    <motion.span
                      className={`block ${i === 1 ? "italic text-white/60" : ""}`}
                      initial={{ y: "110%" }}
                      whileInView={{ y: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 1.1, delay: 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </h2>
            </FadeUp>
          </div>
        </div>

        <div>
          {CHAPTERS.map((c, i) => (
            <Chapter key={c.n} c={c} i={i} lang={lang} />
          ))}
        </div>
      </div>
    </section>
  );
}
